import type { Query, QueryClient } from '@tanstack/react-query'

/**
 * Which React Query reads the offline-capable shells (desktop app, Bhookly
 * Server APK — see lib/offline/shell.ts) persist to IndexedDB, and for how long
 * they must stay in memory so that persisting them means anything.
 *
 * Only slow-changing REFERENCE data is persisted for offline use — never
 * transactional/live data (orders, payments, dashboard, reports), which would
 * be misleading if shown stale. The Electron shell clears all storage on a
 * tenant re-point, so the cache can't bleed across restaurants; the Android
 * WebView needs no such step because its storage is per-origin and each tenant
 * is its own origin.
 */
export const PERSIST_PREFIXES = new Set([
  'settings',
  'categories',
  'admin-categories',
  'products',
  'admin-products',
  'menu',
  // F-FE-N125: the option catalogue. Ids and display prices for the
  // configurator; the offline queue stores option ids only, so nothing cached
  // here can set a price. Without it a reload mid-outage refuses every dish
  // that has options ("Still loading this menu's options").
  'menu-modifiers',
  'stations',
  'kitchen-stations',
  'tables',
  // F-MM-N28: the tenant's real tax / service / POS-fee settings. Without it a
  // reload during a network blip left the counter's on-screen previews on the
  // hardcoded fallback (10% service) while the menu came back from disk.
  // Printed / customer-facing money never reads this — it is server-priced
  // per order (GET /counter/orders/:id/prebill).
  'counterPricing',
])

/** How long a persisted read is trusted on disk. */
export const PERSIST_MAX_AGE_MS = 1000 * 60 * 60 * 24

/**
 * Persist a reference read only once it holds data (F-FE-N175).
 *
 * query-core dehydrates a still-PENDING query with a live Promise attached;
 * IndexedDB cannot store a Promise, so a single loading menu query made the
 * whole persist write throw DataCloneError and nothing was saved — on every
 * fresh start, exactly when a till needs the offline cache. "Has data" rather
 * than the library's success-only default, so a menu whose refetch FAILED
 * during an outage (status 'error', data kept) stays on disk.
 */
export function shouldPersistQuery(query: Query): boolean {
  const first = query.queryKey?.[0]
  return typeof first === 'string' && PERSIST_PREFIXES.has(first) && query.state?.data !== undefined
}

/**
 * Keep persisted reads in memory as long as they are kept on disk (F-FE-N115).
 *
 * The persister only dehydrates queries that are still in the cache. With the
 * default 5-minute gcTime, a menu the cashier had left behind on the Day Close
 * screen was garbage-collected, the next persist wrote IndexedDB without it,
 * and the counter came back cold — on a link that then could not complete the
 * fetch (KIMMUN, 2026-09-03). The offline banner's "showing last saved menu"
 * was true for five minutes. gcTime must be >= maxAge for the promise to hold.
 */
export function applyPersistGcTime(client: QueryClient): void {
  for (const prefix of PERSIST_PREFIXES) {
    client.setQueryDefaults([prefix], { gcTime: PERSIST_MAX_AGE_MS })
  }
}
